/**
 * Formats numbers in the Spanish/European style with dots as thousand separators and commas as decimal separators.
 * Example: 1827.50 -> "1.827,50"
 *
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default is 2)
 * @returns The formatted number as a string
 */
export const formatNumber = (value: number | string | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined || value === '') {
        return decimals === 0 ? '0' : '0,00';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (Number.isNaN(numValue)) {
        return decimals === 0 ? '0' : '0,00';
    }

    // Use Intl.NumberFormat with Spanish locale for consistent formatting
    return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
    }).format(numValue);
};

/**
 * Formats a monetary value in the Spanish/European style.
 * Example: 1827.50 -> "1.827,50"
 *
 * @param value - The numeric value to format
 * @returns The formatted number as a string with 2 decimals
 */
export const formatCurrency = (value: number | string | null | undefined): string => formatNumber(value, 2);

/**
 * Formats an integer with Spanish thousand separators.
 * Example: 1827 -> "1.827"
 *
 * @param value - The numeric value to format
 * @returns The formatted number as a string with no decimals
 */
export const formatInteger = (value: number | string | null | undefined): string => formatNumber(value, 0);
